import { DEFAULT_SHOPPER_AGENT_PERSONA_NAME } from "@redotech/redo-model/shopper-agent/persona-config";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef } from "react";
import {
  getTitleContentRevealDelayMs,
  StepMotionGroup,
  StepMotionItem,
} from "./step-motion";
import { StepTitle } from "./step-title";

export interface PersonaStepProps {
  animateItems?: boolean;
  stepNumber: number;
  personaName: string;
  setPersonaName(value: string): void;
}

export const PersonaStep = memo(function PersonaStep({
  animateItems,
  stepNumber,
  personaName,
  setPersonaName,
}: PersonaStepProps) {
  const title = "Give your Concierge a name";
  const contentRevealDelayMs = getTitleContentRevealDelayMs(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => {
        inputRef.current?.focus();
        inputRef.current?.select();
      },
      animateItems ? contentRevealDelayMs + 180 : 0,
    );

    return () => window.clearTimeout(timeout);
  }, [animateItems, contentRevealDelayMs]);

  return (
    <StepMotionGroup animateItems={animateItems}>
      <Flex dir="column" gap="lg">
        <StepMotionItem animateItems={animateItems}>
          <Text fontSize="sm" textColor="quaternary">
            Step {stepNumber}
          </Text>
        </StepMotionItem>
        <StepMotionItem animateItems={animateItems}>
          <StepTitle animate={animateItems} text={title} />
        </StepMotionItem>
        <StepMotionItem
          animateItems={animateItems}
          delayMs={contentRevealDelayMs}
        >
          <Text fontSize="lg" textColor="quaternary">
            This is how Concierge will introduce itself in customer
            conversations. We&apos;ll start with{" "}
            {DEFAULT_SHOPPER_AGENT_PERSONA_NAME}, but you can choose any name
            that feels natural for your brand.
          </Text>
        </StepMotionItem>
        <StepMotionItem
          animateItems={animateItems}
          delayMs={contentRevealDelayMs}
        >
          <RedoTextInput
            fullWidth
            label="Concierge name"
            placeholder={`e.g. ${DEFAULT_SHOPPER_AGENT_PERSONA_NAME}`}
            ref={inputRef}
            setValue={setPersonaName}
            size="md"
            value={personaName}
          />
        </StepMotionItem>
      </Flex>
    </StepMotionGroup>
  );
});
